import {
  Anchor,
  Box,
  Button,
  Center,
  CopyButton,
  Grid,
  Group,
  Loader,
  Paper,
  Text,
} from '@mantine/core';
import { PiArrowLeft, PiCheck, PiLink, PiPaperPlaneTilt } from 'react-icons/pi';
import { Link, useParams } from 'wouter';
import {
  InvoiceDocument,
  InvoiceUtilities,
} from '../InvoiceDetailsModal/InvoiceDetailsModal';
import { StatusHeaderBar } from '../StatusHeaderBar/StatusHeaderBar';
import { useInvoiceActions } from './useInvoiceActions';
import { type InvoiceDetails, useInvoiceDetails } from '../../models/invoices';
import { useMockSplitSchedule } from '../../pages/offers/mock/mockSplitSchedule';
import {
  isUpcomingInvoice,
  mergeMockInvoices,
} from '../../lib/mockInvoiceMerge';
import { listUrl } from '@/lib/listMemory';
import { TestBadge } from '@/components/TestBadge';

const INVOICE_STATUS_COLOR: Record<string, string> = {
  issued: 'blue',
  'confirmed-by-client': 'indigo',
  paid: 'teal',
  'partially-paid': 'yellow',
  cancelled: 'gray',
};

const statusLabelOf = (s: string) =>
  s.charAt(0).toUpperCase() + s.slice(1).replace(/-/g, ' ');

// Shareable, full-page invoice details. Resolves real invoices by id; mock split
// installments (negative synthetic ids) are reconstructed from their original
// invoice + the local mock schedule.
export default function InvoiceDetailsPage() {
  const { id } = useParams();
  const idNum = Number(id);
  const isMock = Number.isFinite(idNum) && idNum < 0;
  const realId = !isMock ? (id ?? '') : '';
  const baseId = isMock ? String(Math.floor(Math.abs(idNum) / 1000)) : '';

  const realQ = useInvoiceDetails(realId);
  const baseQ = useInvoiceDetails(baseId);
  const schedules = useMockSplitSchedule((s) => s.schedules);
  const issueInstallment = useMockSplitSchedule((s) => s.issueInstallment);
  const actions = useInvoiceActions();

  let invoice: InvoiceDetails | undefined;
  let isLoading: boolean;
  if (isMock) {
    // includeUpcoming so scheduled (not-yet-issued) installment ids resolve too.
    const merged = mergeMockInvoices(
      baseQ.data ? [baseQ.data] : [],
      schedules,
      {
        includeUpcoming: true,
      },
    ) as InvoiceDetails[];
    invoice = merged.find((i) => i.id === idNum);
    isLoading = baseQ.isLoading;
  } else {
    const real = realQ.data;
    // Apply the mock split: an original invoice whose offer has an approved
    // schedule reads as Cancelled (consistent with the related-invoices list).
    invoice = real
      ? ((mergeMockInvoices([real], schedules) as InvoiceDetails[]).find(
          (i) => i.id === real.id,
        ) ?? real)
      : undefined;
    isLoading = realQ.isLoading;
  }

  const upcoming = invoice ? isUpcomingInvoice(invoice) : false;
  const handleIssueNow = () => {
    if (!invoice?.offerId) return;
    const partIdx = (Math.abs(idNum) % 1000) - 1;
    const instId = schedules[invoice.offerId]?.installments[partIdx]?.id;
    if (instId) issueInstallment(invoice.offerId, instId);
  };

  const series = invoice?.installments ?? null;

  return (
    <Box p="md">
      {invoice && (
        <StatusHeaderBar
          statusLabel={upcoming ? 'Scheduled' : statusLabelOf(invoice.status)}
          color={
            upcoming ? 'gray' : (INVOICE_STATUS_COLOR[invoice.status] ?? 'gray')
          }
          title={
            <Group gap="sm" align="baseline" wrap="wrap">
              <Text fz="xl" fw={700}>
                {upcoming
                  ? `Installment ${invoice.partNumber}`
                  : invoice.invoiceNumber}
              </Text>
              {invoice.isTest && <TestBadge size="md" />}
              {series && (
                <Text fz="sm" c="dimmed">
                  Installment {series.seq} of {series.total}
                </Text>
              )}
            </Group>
          }
          description={
            upcoming
              ? 'This installment has not been issued yet — preview of its planned configuration. It auto-issues 1 month before its due date, or now on request.'
              : undefined
          }
          statusExtra={
            upcoming ? (
              <Button
                size="xs"
                leftSection={<PiPaperPlaneTilt size={14} />}
                onClick={handleIssueNow}
              >
                Issue now
              </Button>
            ) : undefined
          }
          subtitles={
            <Group gap="xs" mt={4} wrap="wrap">
              <Text fz="sm" c="dimmed">
                {invoice.contactEmail}
              </Text>
              {invoice.offerId && (
                <>
                  <Text fz="sm" c="dimmed">
                    ·
                  </Text>
                  <Anchor
                    component={Link}
                    to={`~/offers/${invoice.offerId}/general`}
                    fz="sm"
                  >
                    {invoice.offerTitle || `Offer #${invoice.offerId}`}
                  </Anchor>
                </>
              )}
            </Group>
          }
          actions={
            <>
              <Button
                component={Link}
                to={`~${listUrl('/invoices')}`}
                variant="subtle"
                size="xs"
                leftSection={<PiArrowLeft size={14} />}
              >
                Back to invoices
              </Button>
              <CopyButton
                value={
                  typeof window !== 'undefined' ? window.location.href : ''
                }
              >
                {({ copied, copy }) => (
                  <Button
                    size="xs"
                    variant="light"
                    color={copied ? 'teal' : undefined}
                    leftSection={
                      copied ? <PiCheck size={14} /> : <PiLink size={14} />
                    }
                    onClick={copy}
                  >
                    {copied ? 'Copied' : 'Copy link'}
                  </Button>
                )}
              </CopyButton>
            </>
          }
        />
      )}

      {isLoading && (
        <Center py="xl">
          <Loader />
        </Center>
      )}
      {!isLoading && !invoice && <Text c="dimmed">Invoice not found.</Text>}
      {invoice && (
        // Aligned with the Offer page: main document + utilities sidebar.
        <Grid gap="md">
          <Grid.Col span={{ base: 12, lg: 8 }}>
            <Paper withBorder p="lg">
              <InvoiceDocument invoice={invoice} />
            </Paper>
          </Grid.Col>
          <Grid.Col span={{ base: 12, lg: 4 }}>
            <InvoiceUtilities
              invoice={invoice}
              offerId={invoice.offerId ?? undefined}
              {...actions.handlers}
            />
          </Grid.Col>
        </Grid>
      )}
      {actions.modals}
    </Box>
  );
}
